import {
    BuildingOffice2Icon,
    ChevronUpIcon,
    EnvelopeIcon,
    LinkIcon,
    PencilIcon,
    PencilSquareIcon,
    TrashIcon,
} from "@heroicons/react/24/outline";
import {
    AnswerEntityId,
    CommunityId,
    Contact,
    ContactId,
    extractFirstName,
    IAnswer,
    IContact,
    ILocation,
    LayoutSectionKey,
    LayoutSectionParentKey,
    parseEntityId,
    parseLocation,
    QuestionSource,
    ReferenceBusinessId,
    ReferenceContactId,
    ReferralMappings,
    UserId,
} from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { InputFormBlock } from "components/app/input-form-block";
import { NgByline } from "components/byline/ng-byline";
import { NgBylinePill } from "components/byline/ng-byline-pill";
import { ListClient } from "components/clients/list-client";
import {
    ListReferredClients,
    ReferredClientRow,
} from "components/clients/list-referred-clients";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { ContactCard } from "components/contacts/card";
import { AnsweredCustomFields } from "components/details-page-v-next/answered-custom-fields";
import {
    AddressDetail,
    EmailDetail,
    EntityContactDetails,
    EntitySummary,
    PhoneDetail,
    WebsiteDetail,
} from "components/details-page-v-next/entity-contact-details";
import { HeaderPhoneField } from "components/details-page-v-next/header-fields";
import { Activity } from "components/entity-activity/entity-activity";
import { BusinessCardScanFeedbackPrompt } from "components/feedback/business-card-scan-feedback-prompt";
import { FilesControl } from "components/files";
import { SignatureRequestPanel } from "components/files/signature-request/signature-request-panel";
import { Header } from "components/header";
import { Icon } from "components/icon";
import { EntityPageHeader, Stage } from "components/layout";
import { LayoutFormSection } from "components/layout/layout-form-section";
import { LayoutSectionResult } from "components/layout/layout-items";
import { useLayoutSection } from "components/layout/use-layout-section";
import { getMarkerIconColorFromContractStatus } from "components/map/map-common";
import { NextgenEntityMap } from "components/map/nextgen-entity-map";
import { MarkerIconColor } from "components/map/types";
import { ReferenceContactMarketingPlan } from "components/marketing-plan/contact-marketing-plan";
import { EntityConversationFeed } from "components/messages/entity-conversation-feed";
import { MessageCompose } from "components/messages/message-compose";
import { MessagesTabHeader } from "components/messages/messages-tab-header";
import { ImportantNoteBanner } from "components/notes/important-note-banner";
import { trpc } from "components/rpc/trpc";
import { Setting } from "components/setting";
import { ActionBarComponent } from "components/shared/action-bar-component";
import { DetailPageLayout } from "components/shared/detail-page-layout";
import { Col, NgGrid, Row } from "components/shared/grid";
import { PhoneInputE164 } from "components/shared/phone-input-e164";
import { Subnav } from "components/shared/subnav";
import { SubnavItem } from "components/shared/subnav/subnav-types";
import { UserSelect } from "components/shared/user-select";
import { EntityTasks, TaskBadge } from "components/tasks/child";
import { DeleteButton, EditButton, IconButton } from "components/ui/action-button";
import {
    ActionsMenu,
    ActionsMenuAction,
    ActionsMenuSection,
} from "components/ui/actions-menu";
import { AutosavingInput } from "components/ui/autosaving-input";
import { Checkbox } from "components/ui/checkbox";
import { ContactNameInput } from "components/ui/contact-name-input";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { InlineBanner } from "components/ui/inline-banner";
import {
    inlineLabelClasses,
    InlineTextAreaField,
    InlineTextField,
} from "components/ui/inline-field";
import { defaultLinkStyle } from "components/ui/link";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { CrmPersona } from "components/ui/persona";
import { SecondaryButton, SecondaryCompactButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import { InteractionNotes } from "components/unified-timeline/interaction-notes";
import { VisitsSection } from "components/visits/visits-section";
import { WorkflowEntityExecutions } from "components/workflow/workflow-entity-executions";
import {
    AdvancedSearchEntityType,
    FileEntityType,
    GetReferenceContactQuery,
    MutationPatchReferenceContactArgs,
    ReferralBasicsFragment,
    useGetReferenceContactQuery,
    useGetReferralsToContactQuery,
    usePatchReferenceContactMutation,
} from "generated/graphql";
import { produce } from "immer";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useRouteMatch } from "react-router";
import { Link } from "react-router-dom";
import { navigate } from "store/actions";
import { loadLeafElementsForEntity } from "store/actions/entity";
import { createChangeRegionAction } from "store/actions/region";
import { ResponsiveMode } from "store/reducers/responsive";
import {
    useCanSeeReferralSourceOwners,
    useFeature,
    useRegionId,
    useRegions,
    useResponsiveMode,
    useUsers,
} from "store/selectors/hooks";
import { ApplicationState } from "store/state";
import { parseRouteId } from "util/route-id";
import { CreateReferralLinkPanel } from "../create-referral-link-panel";
import { OrganizationSearch } from "../select-reference/organization-search";
import { PipelineStageActionBars } from "./pipeline-stage-picker";
import { referenceContactDetails } from "./reference-details";
import { ReferenceTierActionBar } from "./reference-tier-picker";

type ReferenceContactResult = GetReferenceContactQuery["getReferenceContact"];

const getPreferredName = (reference: ReferenceContactResult) => {
    const contactName = reference.contactEntity.name || "(no name)";

    if (reference.business) {
        return `${contactName} (${reference.business.businessContactEntity.name})`;
    }

    return contactName;
};

interface ReferenceContactHeaderProps {
    reference: ReferenceContactResult;
    onCompose: () => void;
    onDelete: () => void;
    onRequestSignature: () => void;
    onCreateReferralLink: () => void;
}

const ReferenceContactHeader: React.FC<ReferenceContactHeaderProps> = props => {
    const { reference, onCompose, onDelete, onRequestSignature, onCreateReferralLink } =
        props;

    const shareReferralLinkEnabled = useFeature("shareReferralLink");
    const detailsPageVNext = useFeature("detailsPageVNext");
    const name = reference.contactEntity?.name ?? "(no name)";
    const role = reference.contactEntity?.role;
    const organization = referenceContactDetails(reference).organization;
    const vNextSubtext =
        role || organization ? (
            <span className="flex min-w-0 items-center gap-1.5">
                {role ? <span className="truncate">{role}</span> : null}
                {role && organization ? <span aria-hidden="true">·</span> : null}
                {organization ? (
                    <Link
                        to={organization.href}
                        className={`inline-flex min-w-0 items-center gap-1 ${defaultLinkStyle}`}>
                        <BuildingOffice2Icon
                            aria-hidden="true"
                            className="h-4 w-4 flex-shrink-0"
                        />
                        <span className="truncate">{organization.name}</span>
                    </Link>
                ) : null}
            </span>
        ) : undefined;
    return (
        <EntityPageHeader>
            <CrmPersona
                name={name}
                size="auto"
                subtext={
                    detailsPageVNext
                        ? vNextSubtext
                        : reference.business?.businessContactEntity?.name
                }
            />
            <div className="flex items-center space-x-4">
                <div>
                    <NgByline entity={reference} mode="header" />
                </div>
                <div>
                    <ActionsMenu>
                        <ActionsMenuSection>
                            <ActionsMenuAction
                                Icon={EnvelopeIcon}
                                menuHidden={false}
                                onClick={onCompose}>
                                Send email
                            </ActionsMenuAction>
                            <ActionsMenuAction
                                Icon={PencilSquareIcon}
                                menuHidden={false}
                                onClick={onRequestSignature}>
                                Request signature
                            </ActionsMenuAction>
                            <ActionsMenuAction
                                Icon={LinkIcon}
                                menuHidden={!shareReferralLinkEnabled}
                                onClick={onCreateReferralLink}>
                                Share referral link
                            </ActionsMenuAction>
                        </ActionsMenuSection>
                        <ActionsMenuSection>
                            <ActionsMenuAction
                                Icon={TrashIcon}
                                menuHidden={false}
                                onClick={onDelete}>
                                Delete
                            </ActionsMenuAction>
                        </ActionsMenuSection>
                    </ActionsMenu>
                </div>
            </div>
        </EntityPageHeader>
    );
};

interface ReferenceContactSummaryInputProps {
    reference: ReferenceContactResult;
    onChange: (args: MutationPatchReferenceContactArgs) => void;
}

const ReferenceContactSummaryInput: React.FC<
    ReferenceContactSummaryInputProps
> = props => {
    const { reference, onChange } = props;

    return (
        <div>
            <label
                htmlFor="edit-summary-notes"
                className="mb-1 block text-sm font-semibold text-gray-700">
                Contact Summary
            </label>
            <AutosavingInput
                id="edit-summary-notes"
                multiLine={true}
                rows={5}
                autoGrow={false}
                initial={reference.summary}
                onCommit={newVal => onChange({ id: reference.id, summary: newVal })}
            />
        </div>
    );
};

interface ReferenceContactActionBarProps {
    reference: ReferenceContactResult;
    onChange: (args: MutationPatchReferenceContactArgs) => void;
    details: React.ReactNode;
}

const ReferenceContactActionBar: React.FC<ReferenceContactActionBarProps> = props => {
    const { reference, onChange, details } = props;

    const showAssignedTo = useCanSeeReferralSourceOwners();
    const userState = useUsers();

    const referenceLocation: ILocation | null = useMemo(() => {
        if (reference.business) {
            return parseLocation(
                reference.business.latitude,
                reference.business.longitude,
            ).getOrElse(null);
        }

        if (reference.community) {
            return parseLocation(
                reference.community.latitude,
                reference.community.longitude,
            ).getOrElse(null);
        }

        return null;
    }, [reference.business, reference.community]);

    const markerColor: MarkerIconColor = useMemo(() => {
        if (reference.community) {
            return getMarkerIconColorFromContractStatus(
                reference.community.contractStatus,
            );
        }

        return "violet";
    }, [reference.community]);

    let ownerName = "Unassigned";

    if (reference.ownerId && userState.users[reference.ownerId]) {
        ownerName = userState.users[reference.ownerId].name;
    }

    return (
        <div className="action-bar p-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                <div className="flex-grow space-y-3">
                    {details}
                    <div className="flex flex-wrap items-center gap-2">
                        {showAssignedTo && (
                            <ActionBarComponent label="Assigned to" value={ownerName}>
                                <UserSelect
                                    includeEveryone={false}
                                    includeUnassigned={true}
                                    label="Assigned to"
                                    onChange={newVal =>
                                        onChange({
                                            id: reference.id,
                                            ownerId: (newVal as UserId) || null,
                                        })
                                    }
                                    value={reference.ownerId || ""}
                                />
                            </ActionBarComponent>
                        )}
                        <ReferenceTierActionBar
                            sourceType="contact"
                            sourceId={reference.id}
                        />
                        <PipelineStageActionBars
                            entityType="ReferenceContact"
                            entityId={reference.id}
                        />
                        <NgBylinePill entity={reference} mode="action-bar" />
                    </div>
                </div>
                {referenceLocation ? (
                    <div className="hidden w-80 flex-shrink-0 overflow-hidden rounded-lg shadow-sm sm:block lg:w-96">
                        <NextgenEntityMap
                            entityId={reference.business?.id || reference.community?.id}
                            iconType={markerColor}
                            location={referenceLocation}
                            zoom={11}
                        />
                    </div>
                ) : null}
            </div>
        </div>
    );
};

interface ReferenceContactEntityFormProps {
    reference: ReferenceContactResult;
    onChange: (args: MutationPatchReferenceContactArgs) => void;
    onAnswerCommitted: (entityId: AnswerEntityId, answer: IAnswer) => void;
    layoutSection: LayoutSectionResult | null;
}

const ReferenceContactEntityForm: React.FC<ReferenceContactEntityFormProps> = props => {
    const { reference, onChange, onAnswerCommitted, layoutSection } = props;

    const contact = reference.contactEntity;
    return (
        <InputFormBlock additionalClassName="contacts">
            <Header iconName="40_ReferralPerson">Contact Info</Header>
            <div className="space-y-2 lg:space-y-4">
                <ContactNameInput
                    name={contact.name}
                    preferredName={contact.preferredName}
                    onCommit={(fieldName, newValue) =>
                        onChange({
                            id: reference.id,
                            contactEntity: { [fieldName]: newValue },
                        })
                    }
                />
                <AutosavingInput
                    label="Title / Role"
                    initial={contact.role}
                    onCommit={newVal =>
                        onChange({ id: reference.id, contactEntity: { role: newVal } })
                    }
                />
                <div className="flex items-center space-x-2 lg:space-x-4">
                    <PhoneInputE164
                        label="Cell Phone"
                        initial={contact.cellPhone}
                        onCommit={newVal =>
                            onChange({
                                id: reference.id,
                                contactEntity: { cellPhone: newVal },
                            })
                        }
                        entityId={reference.id}
                    />
                    <PhoneInputE164
                        label="Alternate Phone"
                        initial={contact.phone1}
                        onCommit={newVal =>
                            onChange({
                                id: reference.id,
                                contactEntity: { phone1: newVal },
                            })
                        }
                        entityId={reference.id}
                    />
                </div>
                <AutosavingInput
                    label="Email Address"
                    initial={contact.email1}
                    onCommit={newVal =>
                        onChange({ id: reference.id, contactEntity: { email1: newVal } })
                    }
                />
                <Setting name="emailOptOut">
                    <Checkbox
                        checked={contact.email1OptOut}
                        label="Email Opt-out (for bulk/newsletter emails)"
                        onChange={e =>
                            onChange({
                                id: reference.id,
                                contactEntity: { email1OptOut: e.target.checked },
                            })
                        }
                    />
                </Setting>
            </div>
            <LayoutFormSection
                entityId={reference.id}
                entity={reference}
                entityType="ReferenceContact"
                layoutSection={layoutSection}
                mode="fieldsonly"
                answerMode={QuestionSource.Entity}
                onAnswerCommitted={onAnswerCommitted}
            />
        </InputFormBlock>
    );
};

interface ContactOrganizationHeaderProps {
    reference: ReferenceContactResult;
    onChange: (args: MutationPatchReferenceContactArgs) => void;
}

const ContactOrganizationHeader: React.FC<ContactOrganizationHeaderProps> = props => {
    const { reference, onChange } = props;

    const [showModal, setShowModal] = useState(false);
    const iconName = reference.community ? "57_Community" : "39_Business";
    const title = reference.community
        ? "Associated Community Info"
        : "Associated Organization Info";

    const handleClear = useCallback(() => {
        onChange({ id: reference.id, communityId: null, businessId: null });
    }, [onChange, reference]);

    const handleChoose = useCallback(
        async (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
            e.preventDefault();
            setShowModal(true);
        },
        [setShowModal],
    );

    const hide = useCallback(() => {
        setShowModal(false);
    }, [setShowModal]);

    const handleCommunity = useCallback(
        (community: CommunityId) => {
            onChange({ id: reference.id, communityId: community, businessId: null });
            hide();
        },
        [onChange, reference, hide],
    );

    const handleBusiness = useCallback(
        (business: ReferenceBusinessId) => {
            onChange({ id: reference.id, communityId: null, businessId: business });
            hide();
        },
        [onChange, reference, hide],
    );

    return (
        <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
                <div className="w-7">
                    <Icon name={iconName} />
                </div>
                <span className="text-lg text-gray-700">{title}</span>
            </div>
            <div className="space-x-2 flex items-center">
                {reference.community || reference.business ? (
                    <DeleteButton
                        backgroundColor="bg-white"
                        confirm={{
                            message: "Remove organization from this contact?",
                            title: "Clear organization?",
                            confirmLabel: "Yes, clear",
                        }}
                        onClick={handleClear}
                    />
                ) : null}
                <SecondaryButton onClick={handleChoose}>Choose</SecondaryButton>
            </div>
            <Panel
                isOpen={showModal}
                headerText="Choose Organization"
                type={PanelType.extraLarge}
                onDismiss={hide}>
                <OrganizationSearch
                    onCommunity={handleCommunity}
                    onBusiness={handleBusiness}
                />
            </Panel>
        </div>
    );
};

const ContactOrganizationCard: React.FC<{
    reference: ReferenceContactResult;
}> = props => {
    const { reference } = props;

    if (reference.business) {
        return (
            <ContactCard
                parentEntityType={AdvancedSearchEntityType.ReferenceBusiness}
                parentId={reference.business.id}
                href={`/references/organizations/show/${reference.business.id}`}
                contact={Contact.load(reference.business.businessContactEntity)}
            />
        );
    }

    if (reference.community) {
        const communityAsContact: IContact = Contact.load({
            id: parseEntityId<ContactId>(reference.community.id),
            name: reference.community.name,
            address1: reference.community.address,
            city: reference.community.city,
            state: reference.community.state,
            zip: reference.community.zip,
            phone1: reference.community.mainPhone,
            website: reference.community.website,
            email1: reference.community.email,
        });

        return (
            <ContactCard
                parentEntityType={AdvancedSearchEntityType.Community}
                parentId={reference.community.id}
                href={`/communities/show/${reference.community.id}`}
                contact={communityAsContact}
            />
        );
    }

    return <p>This contact is not currently associated with an Organization</p>;
};

interface ContactOrganizationPanelProps {
    reference: ReferenceContactResult;
    onChange: (args: MutationPatchReferenceContactArgs) => void;
}

const ContactOrganizationPanel: React.FC<ContactOrganizationPanelProps> = props => {
    const { reference, onChange } = props;

    if (!reference) {
        return null;
    }

    return (
        <InputFormBlock>
            <ContactOrganizationHeader reference={reference} onChange={onChange} />
            <ContactOrganizationCard reference={reference} />
        </InputFormBlock>
    );
};

interface ReferenceContactInboundReferralsProps {
    reference: ReferenceContactResult;
    clients: ReferredClientRow[];
}

const ReferenceContactInboundReferrals: React.FC<
    ReferenceContactInboundReferralsProps
> = props => {
    const { reference, clients } = props;

    const users = useUsers().users;

    if (!reference) {
        return null;
    }

    if (clients.length == 0) {
        return (
            <div>
                <h3>
                    No Inbound Client Referrals have been received from this referral
                    source yet.
                </h3>
                <br />
                <p>
                    Note: when editing a Client profile, if you mark any client&apos;s
                    &quot;Referral Source&quot; as this referral contact, that client will
                    then be listed here.
                </p>
            </div>
        );
    }
    return (
        <div>
            <p>
                All Clients referred to you (or your agency) that have come from this
                referral source ({getPreferredName(reference)}
                ).
            </p>
            <br />

            <ListReferredClients clients={clients} users={users} />
        </div>
    );
};

interface ReferenceContactOutboundReferralsProps {
    outboundClients: ReferralBasicsFragment["placement"][];
}

const ReferenceContactOutboundReferrals: React.FC<
    ReferenceContactOutboundReferralsProps
> = props => {
    const { outboundClients } = props;

    const users = useUsers().users;
    const responsiveMode = useResponsiveMode();

    if (outboundClients.length == 0) {
        return (
            <div>
                <h3>
                    No Outbound Client Referrals have been linked to this referral source
                    yet.
                </h3>
                <br />
                <p>
                    Note: when editing a Client profile, if you add a &quot;Professional
                    Referral&quot; and choose this referral source, that client will then
                    be listed here.
                </p>
                <p>
                    Tracking your Outbound Professional Referrals can help you identify
                    how often you are referring to this referral source vs. how often they
                    are referring to you.
                </p>
            </div>
        );
    }
    return (
        <div>
            <p>
                All Clients you have referred out to this contact (marked as
                &quot;Professional Referrals&quot; on a Client profile).
            </p>
            <br />

            <ListClient
                showAssignedToColumn={false}
                users={users}
                clients={outboundClients}
                tableShort={true}
                responsiveMode={responsiveMode}
                sourceType={"fromReferral"}
            />
        </div>
    );
};

interface ReferenceContactOverviewProps {
    reference: ReferenceContactResult;
    onChange: (args: MutationPatchReferenceContactArgs) => Promise<void>;
    onAnswerCommitted: (entityId: AnswerEntityId, answer: IAnswer) => void;
}

const ReferenceContactOverview: React.FC<ReferenceContactOverviewProps> = props => {
    const { reference, onChange, onAnswerCommitted } = props;

    const responsiveMode = useResponsiveMode();
    const isMobile = responsiveMode < ResponsiveMode.large;

    const contactInformationSection = useLayoutSection(
        LayoutSectionParentKey.ReferenceContact,
        LayoutSectionKey.ReferenceContactContactInformation,
    );
    const additionalInformationSection = useLayoutSection(
        LayoutSectionParentKey.ReferenceContact,
        LayoutSectionKey.ReferenceContactAdditionalInformation,
    );

    if (!reference) {
        return null;
    }

    return (
        <NgGrid>
            <NgGrid.Column>
                {isMobile ? (
                    <InteractionNotes
                        entityId={reference.id}
                        entityType="ReferenceContact"
                    />
                ) : null}
                <ReferenceContactEntityForm
                    reference={reference}
                    onChange={onChange}
                    onAnswerCommitted={onAnswerCommitted}
                    layoutSection={contactInformationSection}
                />
                <ContactOrganizationPanel reference={reference} onChange={onChange} />
            </NgGrid.Column>
            <NgGrid.Column>
                {isMobile ? null : (
                    <InteractionNotes
                        entityId={reference.id}
                        entityType="ReferenceContact"
                    />
                )}
                <ReferenceContactMarketingPlan contactId={reference.id} />
                <LayoutFormSection
                    entityId={reference.id}
                    entity={reference}
                    entityType="ReferenceContact"
                    layoutSection={additionalInformationSection}
                    mode="tile"
                    answerMode={QuestionSource.Entity}
                    onAnswerCommitted={onAnswerCommitted}
                />
                <WorkflowEntityExecutions entity={reference} />
            </NgGrid.Column>
        </NgGrid>
    );
};

interface ReferenceContactHeaderFieldsProps {
    reference: ReferenceContactResult;
    onChange: (args: MutationPatchReferenceContactArgs) => void;
    onAnswerCommitted: (entityId: AnswerEntityId, answer: IAnswer) => void;
}

interface ContactHeaderPartProps {
    reference: ReferenceContactResult;
    onChange: (args: MutationPatchReferenceContactArgs) => void;
}

const ContactPreferredNameRow: React.FC<ContactHeaderPartProps> = props => {
    const { reference, onChange } = props;

    const contact = reference.contactEntity;
    const [editing, setEditing] = useState(false);
    const startEditing = useCallback(() => setEditing(true), []);
    const stopEditing = useCallback(() => setEditing(false), []);

    if (!editing) {
        return (
            <div className="flex items-center space-x-1">
                <p className="text-xs text-gray-500">
                    Preferred name: {contact.preferredName}
                </p>
                <EditButton
                    tabIndex={-1}
                    size="sm"
                    backgroundColor="bg-white"
                    onClick={startEditing}
                />
            </div>
        );
    }

    return (
        <InlineTextField
            label={
                <span className="flex items-center space-x-2">
                    <span>Preferred name</span>
                    <IconButton
                        size="sm"
                        backgroundColor="bg-white"
                        Icon={ChevronUpIcon}
                        onClick={stopEditing}
                    />
                </span>
            }
            initial={contact.preferredName}
            onCommit={newVal =>
                onChange({
                    id: reference.id,
                    contactEntity: {
                        preferredName: newVal || extractFirstName(contact.name),
                    },
                })
            }
        />
    );
};

const contactOrganizationTypeLine = (
    reference: ReferenceContactResult,
): string | null => {
    const contactType = reference.business?.businessContactEntity?.contactType;

    if (!contactType) {
        return null;
    }

    const mapping = ReferralMappings[contactType];

    return mapping ? `${mapping.category} » ${mapping.humanReadable}` : contactType;
};

const ContactOrganizationTile: React.FC<ContactHeaderPartProps> = props => {
    const { reference, onChange } = props;

    const [choosing, setChoosing] = useState(false);
    const openChooser = useCallback(() => setChoosing(true), []);
    const dismissChooser = useCallback(() => setChoosing(false), []);
    const handleCommunity = useCallback(
        (community: CommunityId) => {
            setChoosing(false);
            onChange({ id: reference.id, communityId: community, businessId: null });
        },
        [onChange, reference.id],
    );
    const handleBusiness = useCallback(
        (business: ReferenceBusinessId) => {
            setChoosing(false);
            onChange({ id: reference.id, communityId: null, businessId: business });
        },
        [onChange, reference.id],
    );
    const handleClear = useCallback(() => {
        onChange({ id: reference.id, communityId: null, businessId: null });
    }, [onChange, reference.id]);

    const organization = referenceContactDetails(reference).organization;
    const typeLine = contactOrganizationTypeLine(reference);

    return (
        <div>
            <div className="flex items-center gap-2">
                <span className={inlineLabelClasses}>Associated Organization</span>
                <div className="flex flex-none items-center gap-1">
                    <SecondaryCompactButton onClick={openChooser}>
                        Choose
                    </SecondaryCompactButton>
                    {organization ? (
                        <DeleteButton
                            size="sm"
                            backgroundColor="bg-white"
                            confirm={{
                                message: "Remove organization from this contact?",
                                title: "Clear organization?",
                                confirmLabel: "Yes, clear",
                            }}
                            onClick={handleClear}
                        />
                    ) : null}
                </div>
            </div>
            {organization ? (
                <div className="space-y-1.5 py-1 text-sm">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                        <Link
                            to={organization.href}
                            className={`font-medium ${defaultLinkStyle}`}>
                            {organization.name}
                        </Link>
                        {typeLine ? (
                            <span className="text-xs text-gray-500">{typeLine}</span>
                        ) : null}
                    </div>
                    <div className="flex flex-col items-start gap-x-5 gap-y-1.5 sm:flex-row sm:flex-wrap sm:items-center">
                        {organization.phone ? (
                            <PhoneDetail
                                phone={{ label: null, value: organization.phone }}
                                entityId={reference.id}
                            />
                        ) : null}
                        {organization.email ? (
                            <EmailDetail
                                value={organization.email}
                                optOut={false}
                                entityId={reference.id}
                                entityType={AdvancedSearchEntityType.ReferenceContact}
                            />
                        ) : null}
                        {organization.address ? (
                            <AddressDetail value={organization.address} />
                        ) : null}
                        {organization.website ? (
                            <WebsiteDetail value={organization.website} />
                        ) : null}
                    </div>
                </div>
            ) : (
                <p className="py-1 text-sm text-gray-600">
                    This contact is not currently associated with an Organization
                </p>
            )}
            <Panel
                isOpen={choosing}
                headerText="Choose Organization"
                type={PanelType.extraLarge}
                onDismiss={dismissChooser}>
                <OrganizationSearch
                    onCommunity={handleCommunity}
                    onBusiness={handleBusiness}
                />
            </Panel>
        </div>
    );
};

const ReferenceContactHeaderFields: React.FC<
    ReferenceContactHeaderFieldsProps
> = props => {
    const { reference, onChange, onAnswerCommitted } = props;

    const contact = reference.contactEntity;
    const contactInformationSection = useLayoutSection(
        LayoutSectionParentKey.ReferenceContact,
        LayoutSectionKey.ReferenceContactContactInformation,
    );

    return (
        <div className="form-compact grid grid-cols-1 items-start gap-x-4 gap-y-2 lg:grid-cols-2">
            <div className="space-y-1">
                <InlineTextField
                    label="Name"
                    initial={contact.name}
                    onCommit={newVal =>
                        onChange({ id: reference.id, contactEntity: { name: newVal } })
                    }
                />
                <ContactPreferredNameRow reference={reference} onChange={onChange} />
            </div>
            <InlineTextField
                label="Title / Role"
                initial={contact.role}
                onCommit={newVal =>
                    onChange({ id: reference.id, contactEntity: { role: newVal } })
                }
            />
            <HeaderPhoneField
                label="Cell Phone"
                initial={contact.cellPhone}
                onCommit={newVal =>
                    onChange({ id: reference.id, contactEntity: { cellPhone: newVal } })
                }
                entityId={reference.id}
            />
            <HeaderPhoneField
                label="Alternate Phone"
                initial={contact.phone1}
                onCommit={newVal =>
                    onChange({ id: reference.id, contactEntity: { phone1: newVal } })
                }
                entityId={reference.id}
            />
            <div className="space-y-1">
                <InlineTextField
                    label="Email Address"
                    initial={contact.email1}
                    onCommit={newVal =>
                        onChange({ id: reference.id, contactEntity: { email1: newVal } })
                    }
                />
                <Setting name="emailOptOut">
                    <Checkbox
                        checked={contact.email1OptOut}
                        label="Email Opt-out (for bulk/newsletter emails)"
                        labelClassName="text-xs text-gray-600"
                        onChange={e =>
                            onChange({
                                id: reference.id,
                                contactEntity: { email1OptOut: e.target.checked },
                            })
                        }
                    />
                </Setting>
            </div>
            <LayoutFormSection
                entityId={reference.id}
                entity={reference}
                entityType="ReferenceContact"
                layoutSection={contactInformationSection}
                mode="fieldsonly"
                answerMode={QuestionSource.Entity}
                onAnswerCommitted={onAnswerCommitted}
            />
            <div className="lg:col-span-2">
                <ContactOrganizationTile reference={reference} onChange={onChange} />
            </div>
            <div className="lg:col-span-2">
                <InlineTextAreaField
                    label="Contact Summary"
                    initial={reference.summary}
                    rows={2}
                    onCommit={newVal => onChange({ id: reference.id, summary: newVal })}
                />
            </div>
        </div>
    );
};

const hasContactDetails = (reference: ReferenceContactResult): boolean => {
    const details = referenceContactDetails(reference);

    return !!(
        reference.contactEntity?.name ||
        details.classification ||
        details.phones.length > 0 ||
        details.email ||
        details.address ||
        details.website ||
        details.organization ||
        reference.summary ||
        reference.answers.length > 0
    );
};

interface ReferenceContactDetailsBlockProps {
    reference: ReferenceContactResult;
    onChange: (args: MutationPatchReferenceContactArgs) => void;
    onAnswerCommitted: (entityId: AnswerEntityId, answer: IAnswer) => void;
}

const ReferenceContactDetailsBlock: React.FC<
    ReferenceContactDetailsBlockProps
> = props => {
    const { reference, onChange, onAnswerCommitted } = props;

    const [editing, setEditing] = useState(() => !hasContactDetails(reference));
    const startEditing = useCallback(() => setEditing(true), []);
    const stopEditing = useCallback(() => setEditing(false), []);
    const contactInformationSection = useLayoutSection(
        LayoutSectionParentKey.ReferenceContact,
        LayoutSectionKey.ReferenceContactContactInformation,
    );

    return (
        <div className="space-y-2">
            <div>
                <SecondaryCompactButton
                    onClick={editing ? stopEditing : startEditing}
                    className="flex items-center space-x-1">
                    {editing ? (
                        <>
                            <ChevronUpIcon aria-hidden="true" className="h-3.5 w-3.5" />
                            <span>Collapse</span>
                        </>
                    ) : (
                        <>
                            <PencilIcon aria-hidden="true" className="h-3.5 w-3.5" />
                            <span>Edit contact information</span>
                        </>
                    )}
                </SecondaryCompactButton>
            </div>
            {editing ? (
                <ReferenceContactHeaderFields
                    reference={reference}
                    onChange={onChange}
                    onAnswerCommitted={onAnswerCommitted}
                />
            ) : (
                <>
                    <EntityContactDetails
                        entityId={reference.id}
                        entityType={AdvancedSearchEntityType.ReferenceContact}
                        details={referenceContactDetails(reference)}
                    />
                    <AnsweredCustomFields
                        entityType={AdvancedSearchEntityType.ReferenceContact}
                        layoutSection={contactInformationSection}
                        answers={reference.answers}
                    />
                    <EntitySummary summary={reference.summary} />
                </>
            )}
        </div>
    );
};

interface ReferenceContactOverviewVNextProps {
    reference: ReferenceContactResult;
    onAnswerCommitted: (entityId: AnswerEntityId, answer: IAnswer) => void;
}

const ReferenceContactOverviewVNext: React.FC<
    ReferenceContactOverviewVNextProps
> = props => {
    const { reference, onAnswerCommitted } = props;

    const additionalInformationSection = useLayoutSection(
        LayoutSectionParentKey.ReferenceContact,
        LayoutSectionKey.ReferenceContactAdditionalInformation,
    );

    if (!reference) {
        return null;
    }

    return (
        <NgGrid>
            <NgGrid.Column>
                <InteractionNotes entityId={reference.id} entityType="ReferenceContact" />
            </NgGrid.Column>
            <NgGrid.Column>
                <ReferenceContactMarketingPlan contactId={reference.id} />
                <LayoutFormSection
                    entityId={reference.id}
                    entity={reference}
                    entityType="ReferenceContact"
                    layoutSection={additionalInformationSection}
                    mode="tile"
                    answerMode={QuestionSource.Entity}
                    onAnswerCommitted={onAnswerCommitted}
                />
                <WorkflowEntityExecutions entity={reference} />
            </NgGrid.Column>
        </NgGrid>
    );
};

interface ShowReferenceContactInternalProps {
    referenceId: ReferenceContactId;
    subpage?: string;
}

const ShowReferenceContactInternal: React.FC<
    ShowReferenceContactInternalProps
> = props => {
    const { referenceId, subpage } = props;

    const placementEnabled = useFeature("placement");
    const detailsPageVNext = useFeature("detailsPageVNext");

    const getReferenceContactQuery = useGetReferenceContactQuery(
        { id: referenceId },
        { keepPreviousData: true },
    );
    const getOutboundReferralsQuery = useGetReferralsToContactQuery(
        {
            referenceContactId: referenceId,
        },
        { enabled: placementEnabled },
    );
    const reference = getReferenceContactQuery.data?.getReferenceContact;
    const patchReferenceContactMutation = usePatchReferenceContactMutation();
    const [composing, setComposing] = useState(false);
    const [requestSignature, setReqesutSignature] = useState(false);
    const [createReferralLink, setCreateReferralLink] = useState(false);
    const client = useQueryClient();
    const files = useSelector(
        (state: ApplicationState) =>
            (state.files.files && state.files.files[referenceId]) || [],
    );
    const dispatch = useDispatch();

    const selectedReferenceRegionId = useRegionId("reference");
    const regions = useRegions();

    useEffect(() => {
        loadLeafElementsForEntity(referenceId, dispatch);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (
            selectedReferenceRegionId &&
            reference?.regionId &&
            selectedReferenceRegionId !== reference?.regionId
        ) {
            const newRegionKey = regions.find(
                r => r.referenceRegionId === reference?.regionId,
            )?.key;
            if (newRegionKey) {
                dispatch(createChangeRegionAction(newRegionKey));
            }
        }
    }, [reference?.regionId, selectedReferenceRegionId]); // eslint-disable-line react-hooks/exhaustive-deps

    const trpcClient = trpc.useTRPC();
    const getReferredClientsQuery = useQuery({
        ...trpcClient.referralSource.listReferredClients.queryOptions({
            kind: "contact",
            id: referenceId,
        }),
        enabled: Boolean(referenceId),
    });

    const inboundClients: ReferredClientRow[] = useMemo(
        () => getReferredClientsQuery.data ?? [],
        [getReferredClientsQuery.data],
    );

    const outboundClients = useMemo(() => {
        if (!referenceId || !getOutboundReferralsQuery.data) {
            return [];
        }

        return getOutboundReferralsQuery.data.getReferralsToContact
            .map(r => r.placement)
            .filter(c => !!c);
    }, [referenceId, getOutboundReferralsQuery.data]);

    const handleReferenceChange = useCallback(
        async (args: MutationPatchReferenceContactArgs) => {
            const result = await patchReferenceContactMutation.mutateAsync(args);

            if (result?.patchReferenceContact) {
                client.setQueryData<GetReferenceContactQuery>(
                    ["getReferenceContact", { id: referenceId }],
                    oldData => {
                        return produce(oldData, draft => {
                            if (draft) {
                                draft.getReferenceContact = result.patchReferenceContact;
                            }
                        });
                    },
                );
            }
        },
        [patchReferenceContactMutation, client, referenceId],
    );

    const handleAnswerCommitted = useCallback(
        (entityId: AnswerEntityId, answer: IAnswer) => {
            getReferenceContactQuery.refetch();
        },
        [getReferenceContactQuery],
    );

    const renderOverview = useCallback(() => {
        if (detailsPageVNext) {
            return (
                <ReferenceContactOverviewVNext
                    reference={reference}
                    onAnswerCommitted={handleAnswerCommitted}
                />
            );
        }

        return (
            <ReferenceContactOverview
                reference={reference}
                onChange={handleReferenceChange}
                onAnswerCommitted={handleAnswerCommitted}
            />
        );
    }, [reference, handleReferenceChange, handleAnswerCommitted, detailsPageVNext]);

    const renderInboundReferrals = useCallback(() => {
        if (!getReferredClientsQuery.data) {
            return <Spinner />;
        }

        return (
            <ReferenceContactInboundReferrals
                reference={reference}
                clients={inboundClients}
            />
        );
    }, [reference, inboundClients, getReferredClientsQuery]);

    const renderOutboundReferrals = useCallback(() => {
        return <ReferenceContactOutboundReferrals outboundClients={outboundClients} />;
    }, [outboundClients]);

    const renderMessages = useCallback(() => {
        return (
            <EntityConversationFeed
                entityId={referenceId}
                entityType={AdvancedSearchEntityType.ReferenceContact}
            />
        );
    }, [referenceId]);

    const renderTasks = useCallback(() => {
        return (
            <Row type="flex" className="collapsible">
                <Col className="w-full">
                    <EntityTasks
                        parentId={referenceId}
                        entityInfo={{
                            displayName: reference?.contactEntity?.name || "(no name)",
                            path: `/references/show-contact/${referenceId}`,
                            kind: "ReferenceContact",
                        }}
                    />
                </Col>
            </Row>
        );
    }, [referenceId, reference]);

    const renderFiles = useCallback(() => {
        return (
            <FilesControl entityId={referenceId} entityType={FileEntityType.Referral} />
        );
    }, [referenceId]);
    const renderVisits = useCallback(() => {
        return (
            <VisitsSection
                parent={{
                    kind: "contact",
                    id: referenceId,
                    name: reference?.contactEntity?.name || "(no name)",
                    address: reference
                        ? referenceContactDetails(reference).address
                        : null,
                }}
            />
        );
    }, [referenceId, reference]);
    const renderActivity = useCallback(() => {
        return <Activity entityType="ReferenceContact" entityId={referenceId} />;
    }, [referenceId]);

    const handleNavigate = useCallback(
        (path: string) => {
            navigate(`/references/contacts/show/${referenceId}/${path}`);
        },
        [referenceId],
    );

    const handleCloseSection = useCallback(() => {
        navigate(`/references/contacts/show/${referenceId}`);
    }, [referenceId]);

    const handleCompose = useCallback(() => {
        setComposing(true);
    }, [setComposing]);

    const handleComposeDismiss = useCallback(() => {
        setComposing(false);
    }, [setComposing]);

    const handleRequestSignature = useCallback(() => {
        setReqesutSignature(true);
    }, []);
    const dismissRequestSignature = useCallback(() => {
        setReqesutSignature(false);
    }, []);

    const handleCreateReferralLink = useCallback(() => {
        setCreateReferralLink(true);
    }, []);
    const dismissCreateReferralLink = useCallback(() => {
        setCreateReferralLink(false);
    }, []);

    const handleDelete = useCallback(async () => {
        if (reference) {
            const shouldDelete = await fancyConfirm(
                `Delete ${getPreferredName(reference)}?`,
                `Really delete ${getPreferredName(reference)}?`,
                "Yes, delete",
                "Cancel",
            );
            if (shouldDelete) {
                await handleReferenceChange({ id: reference.id, deleted: true });
                navigate(`/references`);
            }
        }
    }, [reference, handleReferenceChange]);

    return (
        <QueryRenderer
            query={getReferenceContactQuery}
            name="ShowReferenceContactInternal.getReferenceContact">
            {data => {
                const subpages = [
                    {
                        href: `/references/contacts/show/${referenceId}/overview`,
                        linkText: "Contact Overview",
                        subpage: "overview",
                        render: renderOverview,
                    },
                    {
                        href: `/references/contacts/show/${referenceId}/inbound`,
                        linkText: `Inbound Referrals`,
                        badge: {
                            count: inboundClients.length,
                            type: "info" as const,
                        },
                        subpage: "inbound",
                        render: renderInboundReferrals,
                    },
                    ...(placementEnabled
                        ? [
                              {
                                  href: `/references/contacts/show/${referenceId}/outbound`,
                                  linkText: `Outbound Referrals`,
                                  badge: {
                                      count: outboundClients.length,
                                      type: "info" as const,
                                  },
                                  subpage: "outbound",
                                  render: renderOutboundReferrals,
                              },
                          ]
                        : []),
                    {
                        href: `/references/contacts/show/${referenceId}/visits`,
                        linkText: "Visits",
                        subpage: "visits",
                        render: renderVisits,
                    },
                    {
                        href: `/references/contacts/show/${referenceId}/emails`,
                        linkText: <MessagesTabHeader />,
                        subpage: "emails",
                        render: renderMessages,
                    },
                    {
                        href: `/references/contacts/show/${referenceId}/tasks`,
                        linkText: "Tasks",
                        subpage: "tasks",
                        render: renderTasks,
                        badge: <TaskBadge parentId={referenceId} />,
                    } as SubnavItem,
                    {
                        href: `/references/contacts/show/${referenceId}/files`,
                        subpage: "files",
                        linkText: `Files`,
                        badge: { count: files.length, type: "info" as const },
                        render: renderFiles,
                    },
                    {
                        href: `/references/contacts/show/${referenceId}/history`,
                        subpage: "history",
                        linkText: "History",
                        render: renderActivity,
                    },
                ];

                return reference.deleted ? (
                    <InlineBanner type="error">
                        This contact has been deleted and its information is no longer
                        available
                    </InlineBanner>
                ) : (
                    <>
                        <ReferenceContactHeader
                            reference={data.getReferenceContact}
                            onCompose={handleCompose}
                            onDelete={handleDelete}
                            onRequestSignature={handleRequestSignature}
                            onCreateReferralLink={handleCreateReferralLink}
                        />
                        {composing ? (
                            <MessageCompose
                                entityId={referenceId}
                                entityType={AdvancedSearchEntityType.ReferenceContact}
                                onDismiss={handleComposeDismiss}
                            />
                        ) : null}
                        <SignatureRequestPanel
                            fileEntityType={FileEntityType.Referral}
                            entityId={referenceId}
                            onDismiss={dismissRequestSignature}
                            isOpen={requestSignature}
                        />
                        {createReferralLink ? (
                            <CreateReferralLinkPanel
                                referenceBusinessId={reference.businessId}
                                referenceContactId={reference.id}
                                referenceCommunityId={reference.communityId}
                                onDismiss={dismissCreateReferralLink}
                            />
                        ) : null}
                        <ReferenceContactActionBar
                            reference={data.getReferenceContact}
                            onChange={handleReferenceChange}
                            details={
                                detailsPageVNext ? (
                                    <ReferenceContactDetailsBlock
                                        key={data.getReferenceContact.id}
                                        reference={data.getReferenceContact}
                                        onChange={handleReferenceChange}
                                        onAnswerCommitted={handleAnswerCommitted}
                                    />
                                ) : (
                                    <ReferenceContactSummaryInput
                                        reference={data.getReferenceContact}
                                        onChange={handleReferenceChange}
                                    />
                                )
                            }
                        />

                        <BusinessCardScanFeedbackPrompt
                            referenceContactId={referenceId}
                        />

                        <ImportantNoteBanner entityId={referenceId} />
                        <DetailPageLayout>
                            <Subnav
                                subpages={subpages}
                                selectedSubpage={subpage}
                                defaultSubpage="overview"
                                navigate={handleNavigate}
                                closeSection={handleCloseSection}
                            />
                        </DetailPageLayout>
                    </>
                );
            }}
        </QueryRenderer>
    );
};

type ShowContactReferenceRouteMatch = {
    params?: { referenceId: string; subpage?: string };
} | null;

export const ShowContactReference: React.FC<unknown> = () => {
    const routeMatch: ShowContactReferenceRouteMatch = useRouteMatch();

    const referenceId = routeMatch?.params?.referenceId
        ? parseRouteId<ReferenceContactId>(
              "ReferenceContact",
              routeMatch.params.referenceId,
          )
        : null;

    if (referenceId) {
        return (
            <Stage>
                <ShowReferenceContactInternal
                    referenceId={referenceId}
                    subpage={routeMatch.params?.subpage}
                />
            </Stage>
        );
    }

    return null;
};
